/**
 * Public bid/budget approval page (§A5). Rendered at /approve/:slug. No auth, no
 * AppShell. The recipient arrives from the email's tokenized link, reviews the
 * full project — scope, schedule, site, and the cost breakdown (shown only when
 * the supervisor opted in) — and either approves (which schedules the project)
 * or requests a change.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  FileText,
  Loader2,
  MapPin,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import type { CrewRoleLines } from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { formatDate } from '@/lib/format';
import { BudgetCoverage } from '@/components/bid/BudgetCoverage';

interface ScheduleDay {
  date: string;
  start_time: string;
  end_time: string;
}
interface ApprovalSummary {
  project_title: string;
  client_name: string;
  kind: string;
  site_label: string | null;
  site_notes: string | null;
  date_label: string | null;
  deadline_label: string | null;
  schedule: ScheduleDay[] | null;
  requested_by: string | null;
  scope: { type: string; title: string; crew: number; hours: number }[];
  notes: string | null;
  /** What the budget covers (crew + man-hours, components, no per-line dollars)
   *  + the grand total. null when cost is hidden (date sign-off). */
  budget: {
    labor_hours: number | null;
    crew: number | null;
    roles: CrewRoleLines | null;
    includes_mileage: boolean;
    includes_debris: boolean;
    total_cents: number;
  } | null;
  status: string;
  approved: boolean;
}

/** '08:00' → '8:00 AM'. */
function hm(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ampm = (h ?? 0) < 12 ? 'AM' : 'PM';
  const h12 = ((h ?? 0) % 12) || 12;
  return `${h12}:${String(m ?? 0).padStart(2, '0')} ${ampm}`;
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen grid place-items-center bg-fill-soft p-6">{children}</div>;
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof MapPin;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-ink-faint">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <div className="text-sm text-ink">{children}</div>
    </div>
  );
}

export function PublicApproval() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const tokenQS = token ? `?token=${encodeURIComponent(token)}` : '';
  const qc = useQueryClient();
  const [mode, setMode] = useState<'view' | 'feedback'>('view');
  const [note, setNote] = useState('');

  const { data, isLoading, error } = useQuery<ApprovalSummary>({
    queryKey: ['public-approve', slug, token],
    enabled: !!slug,
    retry: false,
    queryFn: () => apiCall<ApprovalSummary>(`/public/approve/${slug}${tokenQS}`),
  });

  const act = useMutation<
    { ok: boolean; status: string; approved: boolean },
    Error,
    { action: 'approve' | 'feedback'; note?: string }
  >({
    mutationFn: (body) => apiCall(`/public/approve/${slug}${tokenQS}`, { method: 'POST', body }),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['public-approve', slug, token] });
      toast.success(
        vars.action === 'approve' ? 'Approved — thank you!' : 'Feedback sent to the Vari team',
      );
      setMode('view');
      setNote('');
    },
    onError: (e) => toast.error(`Failed: ${e.message}`),
  });

  if (isLoading) {
    return (
      <Centered>
        <Loader2 className="w-6 h-6 animate-spin text-ink-faint" />
      </Centered>
    );
  }
  if (error || !data) {
    return (
      <Centered>
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-sm text-ink-dim">
            This approval link is invalid or has expired. Ask the Vari team to resend it.
          </p>
        </div>
      </Centered>
    );
  }

  return (
    <div className="min-h-screen bg-fill-soft">
      <div className="bg-[#0b1f3a] text-white px-5 py-3 font-semibold">Vari FMS</div>
      <div className="max-w-lg mx-auto p-5">
        <div className="bg-bg rounded-xl border border-line p-5 space-y-5">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-wide text-ink-faint">
              Approval requested
            </div>
            <h1 className="text-xl font-semibold mt-0.5">{data.project_title}</h1>
            <p className="text-sm text-ink-dim">
              {data.client_name}
              <span className="capitalize"> · {data.kind.replace(/_/g, ' ')}</span>
            </p>
          </div>

          {data.scope.length > 0 && (
            <Section icon={ClipboardList} title="Scope of work">
              <ul className="space-y-1">
                {data.scope.map((t, i) => (
                  <li key={i} className="flex items-baseline gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-ink-faint shrink-0 w-16">
                      {t.type}
                    </span>
                    <span>{t.title}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section icon={CalendarDays} title="Schedule">
            {data.schedule && data.schedule.length > 0 ? (
              <div className="space-y-0.5">
                {data.schedule.map((s) => (
                  <div key={s.date}>
                    {formatDate(s.date)} · {hm(s.start_time)}–{hm(s.end_time)}
                  </div>
                ))}
              </div>
            ) : (
              <div>{data.date_label ?? 'To be scheduled'}</div>
            )}
            {data.deadline_label && (
              <div className="text-xs text-ink-faint mt-1">
                Hard deadline: {data.deadline_label}
              </div>
            )}
          </Section>

          {data.site_label && (
            <Section icon={MapPin} title="Site">
              <div>{data.site_label}</div>
              {data.site_notes && (
                <div className="text-xs text-ink-faint mt-1 whitespace-pre-wrap">
                  {data.site_notes}
                </div>
              )}
            </Section>
          )}

          {data.notes && (
            <Section icon={FileText} title="Notes">
              <div className="whitespace-pre-wrap">{data.notes}</div>
            </Section>
          )}

          {data.requested_by && (
            <Section icon={User} title="Requested by">
              <div>{data.requested_by}</div>
            </Section>
          )}

          <BudgetCoverage
            showCost={!!data.budget}
            crew={data.budget?.crew ?? null}
            manHours={data.budget?.labor_hours ?? null}
            roles={data.budget?.roles ?? null}
            includesMileage={data.budget?.includes_mileage ?? false}
            includesDebris={data.budget?.includes_debris ?? false}
            totalCents={data.budget?.total_cents ?? 0}
          />

          {data.approved ? (
            <div className="flex items-center gap-2 rounded-md bg-status-complete text-status-complete-fg border border-status-complete-fg/30 px-3 py-2 text-sm">
              <CheckCircle2 className="w-4 h-4 shrink-0" /> Approved — the Vari team has been
              notified.
            </div>
          ) : mode === 'view' ? (
            <div className="space-y-2 pt-1">
              <Button
                className="w-full"
                onClick={() => act.mutate({ action: 'approve' })}
                disabled={act.isPending}
              >
                {act.isPending && <Loader2 className="w-4 h-4 animate-spin" />} Approve
              </Button>
              <button
                type="button"
                className="w-full text-xs text-ink-faint hover:text-ink underline"
                onClick={() => setMode('feedback')}
              >
                Request a change instead
              </button>
            </div>
          ) : (
            <div className="space-y-2 pt-1">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="What needs to change? (date, cost, scope…)"
                className="w-full rounded-lg border border-line bg-fill-soft p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setMode('view')}
                  disabled={act.isPending}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => act.mutate({ action: 'feedback', note: note.trim() || undefined })}
                  disabled={act.isPending}
                >
                  {act.isPending && <Loader2 className="w-4 h-4 animate-spin" />} Send feedback
                </Button>
              </div>
            </div>
          )}
        </div>
        <p className="text-center text-[11px] text-ink-faint mt-3">Vari Field Management System</p>
      </div>
    </div>
  );
}
